import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  TENANT_EXPORT_KEEP,
  backupPassphraseCheck,
  exportCopyText,
  exportEncryptionText,
  exportSizeText,
} from '@teslyn/domain';
import {
  EXPORT_COLUMNS,
  EXPORT_COLUMNS_V1,
  adminError,
  exportFromRow,
  missingColumn,
  type ExportRun,
} from '../admin/api.ts';
import { Button } from '../components/Button.tsx';
import { CheckField, TextField } from '../components/Fields.tsx';
import { PassphraseReminder, reminderDue, useBackupPassphrase, type PassphraseState } from './passphrase.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const BUCKET = 'exports';

const time = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '–';

const REQUEST_ANSWER: Record<string, string> = {
  queued: 'Asked for. The export starts within 10 minutes and shows here when it is done.',
  busy: 'An export is already waiting or running. It shows here when it is done.',
  too_soon: 'You asked for an export less than an hour ago. Wait, or use that one.',
};

/**
 * The backup passphrase (decision 446): set or replaced by a tenant admin, kept in Vault, never shown again. A new
 * one is used from the next export on; older backups keep the one they were made with.
 */
function PassphrasePanel({
  sb,
  tenantId,
  current,
  reload: load,
  editing,
  setEditing,
  onSaved,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly current: PassphraseState;
  readonly reload: () => void;
  readonly editing: boolean;
  readonly setEditing: (v: boolean) => void;
  readonly onSaved: () => void;
}): ReactElement {
  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [kept, setKept] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (current === null) return <Loading what="the backup passphrase" />;
  if (current === 'unavailable')
    return (
      <div className="panel stack">
        <h2 className="heading">Backup passphrase</h2>
        <p className="body muted">
          The backup passphrase can be set once this Teslyn Cloud is updated. Until then exports are stored in
          Teslyn Cloud only.
        </p>
      </div>
    );

  async function save(): Promise<void> {
    const check = backupPassphraseCheck(first, second);
    if (!check.ok) {
      setProblem(check.message);
      return;
    }
    if (!kept) {
      setProblem('Tick that you have saved the passphrase first: Teslyn cannot show it again.');
      return;
    }
    setBusy(true);
    setProblem(null);
    const { error } = await sb.rpc('tenant_backup_passphrase_set', {
      p_tenant: tenantId,
      p_passphrase: first,
    });
    setBusy(false);
    if (error) {
      setProblem(adminError(error));
      return;
    }
    setFirst('');
    setSecond('');
    setKept(false);
    setEditing(false);
    setSaved(
      'Saved. The next export and every one after it is encrypted with this passphrase and copied to your Nextcloud. Earlier backups still open with the passphrase they were made with.',
    );
    load();
    onSaved();
  }

  const set = current.setAt !== null;
  return (
    <section className="panel stack" aria-label="Backup passphrase">
      <h2 className="heading">Backup passphrase</h2>
      <p className="body">
        {set
          ? `Each export is one zip encrypted with AES-256, which opens in 7-Zip with this passphrase. Set ${time(current.setAt)}${current.setBy ? ` by ${current.setBy}` : ''}.`
          : 'No passphrase is set: exports stay in Teslyn Cloud unencrypted and are not copied to your Nextcloud. Set one to encrypt them.'}
      </p>
      {saved && (
        <p className="notice" role="status">
          {saved}
        </p>
      )}
      {!editing ? (
        <div className="row">
          <Button variant={set ? 'quiet' : 'primary'} onClick={() => setEditing(true)}>
            {set ? 'Replace passphrase' : 'Set passphrase'}
          </Button>
        </div>
      ) : (
        <form
          className="stack"
          aria-label={set ? 'Replace the backup passphrase' : 'Set the backup passphrase'}
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <p className="body muted">
            Use at least 16 characters; four or five unrelated words work well. Keep it in your password
            manager: Teslyn cannot show it again, and without it nobody can open the backups, Braynewave
            included.
          </p>
          <TextField
            label="Backup passphrase"
            type="password"
            autoComplete="new-password"
            autoFocus
            value={first}
            onChange={setFirst}
          />
          <TextField
            label="Type it again"
            type="password"
            autoComplete="new-password"
            value={second}
            onChange={setSecond}
            error={problem ?? undefined}
          />
          <CheckField
            label="I have saved this passphrase in our password manager"
            checked={kept}
            onChange={setKept}
          />
          <div className="row">
            <Button variant="primary" type="submit" busy={busy}>
              Save passphrase
            </Button>
            <Button
              variant="quiet"
              onClick={() => {
                setEditing(false);
                setProblem(null);
                setFirst('');
                setSecond('');
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

/** Data exports (tenant admins): the nightly copies of the organisation's data, to download, and "Export now". */
export function ExportsScreen({
  sb,
  tenantId,
  tenantName,
  roles = ['tenant_admin'],
  openPassphrase = false,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantName: string;
  /** The person's roles (the page is for tenant admins; decision 880's reminder is theirs). */
  readonly roles?: readonly string[];
  /** Opened from Home's reminder: the passphrase form is open straight away. */
  readonly openPassphrase?: boolean;
}): ReactElement {
  const passphrase = useBackupPassphrase(sb, tenantId);
  const [editingPassphrase, setEditingPassphrase] = useState(openPassphrase);
  const [runs, setRuns] = useState<ExportRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [files, setFiles] = useState<{ readonly name: string; readonly size: number | null }[] | null>(null);
  const [asking, setAsking] = useState(false);

  const reload = useCallback(() => {
    setError(null);
    const query = (columns: string) =>
      sb
        .from('tenant_export')
        .select(columns)
        .eq('tenant_id', tenantId)
        .order('requested_at', { ascending: false })
        .limit(30);
    void query(EXPORT_COLUMNS)
      // before decision 838's migration is applied, the older columns
      .then(async (r) => (missingColumn(r.error) ? query(EXPORT_COLUMNS_V1) : r))
      .then(({ data, error: e }) => {
        if (e) setError(adminError(e));
        else setRuns(((data ?? []) as unknown as Record<string, unknown>[]).map(exportFromRow));
      });
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  async function exportNow(): Promise<void> {
    setAsking(true);
    const { data, error: e } = await sb.rpc('tenant_export_request', { p_tenant: tenantId });
    setAsking(false);
    if (e) setNotice({ ok: false, text: adminError(e) });
    else {
      const status = (data as { status?: string } | null)?.status ?? '';
      setNotice({ ok: status === 'queued', text: REQUEST_ANSWER[status] ?? 'Asked for.' });
    }
    reload();
  }

  async function show(run: ExportRun): Promise<void> {
    if (open === run.id) {
      setOpen(null);
      return;
    }
    setOpen(run.id);
    setFiles(null);
    const { data, error: e } = await sb.storage.from(BUCKET).list(run.prefix ?? '', { limit: 1000 });
    if (e) {
      setNotice({ ok: false, text: `Could not list the files: ${e.message}` });
      return;
    }
    setFiles(
      ((data ?? []) as { name: string; metadata?: { size?: number } | null }[]).map((f) => ({
        name: f.name,
        size: typeof f.metadata?.size === 'number' ? f.metadata.size : null,
      })),
    );
  }

  async function download(run: ExportRun, name: string): Promise<void> {
    const { data, error: e } = await sb.storage
      .from(BUCKET)
      .createSignedUrl(`${run.prefix}/${name}`, 300, { download: name });
    if (e || !data) {
      setNotice({ ok: false, text: `Could not download ${name}: ${e?.message ?? 'no address'}` });
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener');
  }

  return (
    <section className="page">
      <PageHeader
        title="Data exports"
        subtitle={`A copy of all of ${tenantName}'s data in Teslyn Cloud, made every night. Tenant admins only.`}
      >
        <Button variant="primary" onClick={() => void exportNow()} busy={asking}>
          Export now
        </Button>
      </PageHeader>
      {/* decision 880: until a passphrase is set, the reminder; its button opens the form below */}
      {reminderDue(roles, passphrase.state) && !editingPassphrase && (
        <PassphraseReminder onSet={() => setEditingPassphrase(true)} />
      )}
      <p className="body">
        Each export is one zip holding every table of your organisation’s data (one file per table, one line
        per row, as JSON), the list of your stored files, and a manifest. With a backup passphrase set, the
        zip is encrypted with AES-256 and also copied to your organisation’s Nextcloud, in the folder
        _DB_Backup. Secrets such as access keys and app passwords are never exported. Teslyn Cloud keeps the
        newest {TENANT_EXPORT_KEEP}; the Nextcloud copies stay until you remove them.
      </p>
      <PassphrasePanel
        sb={sb}
        tenantId={tenantId}
        current={passphrase.state}
        reload={passphrase.reload}
        editing={editingPassphrase}
        setEditing={setEditingPassphrase}
        onSaved={reload}
      />
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--warn'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!runs && !error && <Loading what="exports" />}
      {runs && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="label">Day</th>
                <th className="label">Kind</th>
                <th className="label">Result</th>
                <th className="label">Finished</th>
                <th className="label">Tables</th>
                <th className="label">Rows</th>
                <th className="label">Size</th>
                <th className="label">Encryption</th>
                <th className="label">Nextcloud copy</th>
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr>
                  <td colSpan={10} className="muted">
                    No exports yet. The first nightly export runs after 1 am.
                  </td>
                </tr>
              )}
              {runs.map((r) => (
                <tr key={r.id}>
                  <td className="data">{r.day}</td>
                  <td>{r.kind === 'nightly' ? 'Nightly' : 'Asked for'}</td>
                  <td>
                    {r.status === 'done'
                      ? r.pruned
                        ? 'Removed (older than the newest exports)'
                        : 'Done'
                      : r.status === 'failed'
                        ? `Failed: ${r.error ?? 'unknown'}`
                        : r.status === 'running'
                          ? 'Running'
                          : 'Waiting'}
                  </td>
                  <td className="caption">{time(r.finishedAt)}</td>
                  <td className="data">{r.tables ?? '–'}</td>
                  <td className="data">{r.rows ?? '–'}</td>
                  <td className="data">{r.bytes === null ? '–' : exportSizeText(r.bytes)}</td>
                  <td className="caption">{exportEncryptionText(r)}</td>
                  <td className="caption">{exportCopyText(r)}</td>
                  <td className="table__actions">
                    {r.status === 'done' && !r.pruned && r.prefix && (
                      <Button
                        variant="quiet"
                        aria-expanded={open === r.id}
                        aria-label={`Files of the export of ${r.day}`}
                        onClick={() =>
                          void show(r).catch((e: unknown) => setNotice({ ok: false, text: message(e) }))
                        }
                      >
                        {open === r.id ? 'Hide files' : 'Files'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && runs && (
        <div className="panel stack">
          <h2 className="heading">Files of the export of {runs.find((r) => r.id === open)?.day}</h2>
          {!files && <Loading what="the files" />}
          {files && (
            <ul className="stack">
              {files.map((f) => (
                <li key={f.name} className="row">
                  <span className="data">{f.name}</span>
                  <span className="caption muted">{f.size === null ? '' : exportSizeText(f.size)}</span>
                  <Button
                    variant="quiet"
                    aria-label={`Download ${f.name}`}
                    onClick={() =>
                      void download(
                        runs.find((r) => r.id === open)!,
                        f.name,
                      )
                    }
                  >
                    Download
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
